import type { PipelineStage } from "@newark/desk-core";

export interface Workup {
  tenant_id: string;
  desk: string;
  item_id: string;
  stage: PipelineStage;
  close_reason: string | null;
  mail_name: string | null;
  mail_address: string | null;
  address_verified: number;
  notes: string | null;
  score: number | null;
  do_not_contact: number;
  updated_at: string;
}

export interface QueueItem {
  id: string;
  state?: string | null;
  county?: string | null;
  trustee?: string | null;
  sale_date?: string | null;
  property_address?: string | null;
  city?: string | null;
  zip?: string | null;
  lender_servicer?: string | null;
  borrower_name?: string | null;
  source_url?: string | null;
  workup: Workup | null;
  score: number;
  hasMailAddress: boolean;
  factors: Array<{ id: string; label: string; contribution: number }>;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    credentials: "same-origin",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => req<{ authenticated: boolean }>("/api/me"),
  login: (password: string, username?: string) =>
    req<{ ok: boolean }>("/api/login", {
      method: "POST",
      body: JSON.stringify({ password, username }),
    }),
  logout: () => req<{ ok: boolean }>("/api/logout", { method: "POST" }),
  queue: (showClosed: boolean, state?: string) => {
    const q = new URLSearchParams();
    if (showClosed) q.set("show_closed", "1");
    if (state) q.set("state", state);
    return req<{ results: QueueItem[]; source: string; count: number }>(
      `/api/queue?${q}`,
    );
  },
  setStage: (itemId: string, stage: PipelineStage, close_reason?: string) =>
    req<Workup>("/api/stage", {
      method: "POST",
      body: JSON.stringify({ itemId, stage, close_reason }),
    }),
  saveMail: (itemId: string, name: string, address: string, verified: boolean) =>
    req<Workup>("/api/mail-address", {
      method: "POST",
      body: JSON.stringify({ itemId, name, address, verified, source: "manual" }),
    }),
  createLabelBatch: (itemIds: string[], format: string, topLine?: string) =>
    req<{ batch: { id: string; item_count: number; status: string }; skips: unknown[]; included: number }>(
      "/api/labels/batch",
      {
        method: "POST",
        body: JSON.stringify({ itemIds, format, topLine }),
      },
    ),
  listBatches: () =>
    req<{ results: Array<{ id: string; status: string; item_count: number; format: string; created_at: string }> }>(
      "/api/labels/batches",
    ),
  approveBatch: (id: string) =>
    req(`/api/labels/batch/${id}/approve`, { method: "POST", body: "{}" }),
  printBatch: (id: string) =>
    req(`/api/labels/batch/${id}/print`, { method: "POST", body: "{}" }),
  mailBatch: (id: string) =>
    req(`/api/labels/batch/${id}/mail`, { method: "POST", body: "{}" }),
};
